import React, { ReactNode } from 'react'
import { FormTable, FormTableRow } from './FormTable'
import { RegisteredIntegerField, RegisteredNumericField } from '../../../components/fields/NumericField'
import { Stack, Typography } from '@mui/material'
import { ShouldShow, ShouldShowAny } from '../../../components/entities/IssueType'
import { useFormContext, useWatch } from 'react-hook-form'
import { formatInteger } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import VerifiedIcon from '@mui/icons-material/Verified'
import ErrorIcon from '@mui/icons-material/Error'
import { variableLabel } from './variableLabel'
import { FormIssueEntity } from '../types/FormIssueEntity'
import { usePressReleasesExtractions } from './issue-form/PressReleasesExtractionsContext'
import ExtractionSuggestionControls from './issue-form/ExtractionSuggestionControls'

// Mapping between our form field names and the corresponding AI extraction field names.
const outcomeFieldToAiField = {
    unitSubTotal: 'unit_sub_total',
    unitSubWithRights: 'unit_sub_with_rights',
    unitPctSubWithRights: 'unit_pct_sub_with_rights',
    unitSubWithoutRights: 'unit_sub_without_rights',
    unitPctSubWithoutRights: 'unit_pct_sub_without_rights',
    unitSubGuarantor: 'unit_sub_guarantor',
    unitPctSubGuarantor: 'unit_pct_sub_guarantor',
    secondarySub: 'secondary_sub',
    overAllotmentAllocation: 'over_allotment_allocation',
    overAllotmentAllocationSecondary: 'over_allotment_allocation_secondary',
} as const

const outcomePercentageFields = new Set([
    'unitPctSubWithRights',
    'unitPctSubWithoutRights',
    'unitPctSubGuarantor',
] as const)

type OutcomeValueMap = Record<string, number | string | null | undefined>

type OutcomeProvenanceMap = Record<
    string,
    | {
          sourceUrl?: string | null
          source_url?: string | null
          sourcePages?: unknown
          source_pages?: unknown
          quote?: string | null
      }
    | null
    | undefined
>

const getProvenanceByName = (
    fieldName: keyof typeof outcomeFieldToAiField,
    provenanceByField: OutcomeProvenanceMap,
) => {
    if (!provenanceByField) return null
    return provenanceByField[fieldName] ?? provenanceByField[outcomeFieldToAiField[fieldName]] ?? null
}

// Component to display AI suggestion for a specific field, with "Use suggestion" button and source info popover.
// Notes:
// - `showSuggestionDatapoints` toggles whether the suggestion UI is visible.
// - `suggestionText` is a short label shown to the user (e.g. "AI förslag: 12 345").
// - `onUse` should apply the suggested value into the form (via setValue),
//   so validation and dirty/touched state behave normally.
function FieldExtractionSuggestion({
    label,
    value,
    onUse,
    useDisabled,
    provenance,
}: {
    label?: string
    value?: string | number | null
    onUse: () => void
    useDisabled?: boolean
    provenance?: {
        sourceUrl?: string | null
        source_url?: string | null
        sourcePages?: unknown
        source_pages?: unknown
        quote?: string | null
    } | null
}) {
    const suggestionText = value == null ? null : `AI förslag: ${label ? `${label} ` : ''}${value}`
    const sourceUrl = provenance?.sourceUrl || provenance?.source_url || null
    const rawSourcePages = provenance?.sourcePages || provenance?.source_pages || []
    const sourcePages = Array.isArray(rawSourcePages) ? rawSourcePages : [rawSourcePages]
    const quote = provenance?.quote || null

    return (
        <Stack spacing={0.5} sx={{ mt: 0.25 }}>
            <ExtractionSuggestionControls
                suggestionText={suggestionText}
                onUse={onUse}
                disableUse={useDisabled}
                sourceUrl={sourceUrl}
                sourcePages={sourcePages}
                quote={quote}
            />
        </Stack>
    )
}

const getOutcomeValueByName = (fieldName: keyof typeof outcomeFieldToAiField, valuesByField: OutcomeValueMap) => {
    if (!valuesByField) return null
    if (valuesByField[fieldName] !== undefined) return valuesByField[fieldName]

    const aiFieldName = outcomeFieldToAiField[fieldName]
    if (!aiFieldName) return null
    return valuesByField[aiFieldName] ?? null
}

// Formatting logic for AI suggestions, handles percentages and null/undefined values.
const formatOutcomeSuggestionValue = (
    fieldName: keyof typeof outcomeFieldToAiField,
    value: number | string | null,
    locale: string,
) => {
    if (value == null) return null
    if (typeof value === 'string') return value

    if (
        outcomePercentageFields.has(
            fieldName as 'unitPctSubWithRights' | 'unitPctSubWithoutRights' | 'unitPctSubGuarantor',
        )
    ) {
        return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 })
    }

    return value.toLocaleString(locale)
}

const isFieldFilled = (value: unknown) => value !== null && value !== undefined && value !== ''

function OutcomeFields({
    issue,
    noOverAllotmentChecked,
    noSecondaryOfferingChecked,
    noGuarantorsChecked,
    subscriptionQuota,
    calculatedUnitSubTotal,
    calcUnitSubTotalWithinRange,
}: {
    issue: FormIssueEntity
    noOverAllotmentChecked: boolean
    noSecondaryOfferingChecked: boolean
    noGuarantorsChecked: boolean
    subscriptionQuota: ReactNode
    calculatedUnitSubTotal: number | null
    calcUnitSubTotalWithinRange: boolean | null
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const formMethods = useFormContext()
    const { suggestionOfferingOutcome, suggestionProvenanceByField } = usePressReleasesExtractions()
    const control = formMethods.control
    const [
        unitSubWithRights,
        unitPctSubWithRights,
        unitSubWithoutRights,
        unitPctSubWithoutRights,
        unitSubGuarantor,
        unitPctSubGuarantor,
    ] = useWatch({
        control,
        name: [
            'unitSubWithRights',
            'unitPctSubWithRights',
            'unitSubWithoutRights',
            'unitPctSubWithoutRights',
            'unitSubGuarantor',
            'unitPctSubGuarantor',
        ],
    })

    const overAllotmentSizeSecondary = formMethods.watch('overAllotmentSizeSecondary')
    const offeredUnits = parseInt(formMethods.watch('offeredUnits')) || 0
    const abbTarget = parseInt(formMethods.watch('abbTarget')) || 0
    const totalOfferedUnits =
        parseInt(formMethods.watch('offeredUnits')) + parseInt(formMethods.watch('offeredUnitsUnlisted'))

    const allowUnitSubTotal = !!offeredUnits || !!abbTarget || issue.type === 'Guarantor fee issue'

    // Disable "Use suggestion" button if the corresponding field is already filled.
    const isSuggestionUseDisabled = (fieldName: keyof typeof outcomeFieldToAiField) => {
        switch (fieldName) {
            case 'unitSubWithRights':
                return isFieldFilled(unitPctSubWithRights)
            case 'unitPctSubWithRights':
                return isFieldFilled(unitSubWithRights)
            case 'unitSubWithoutRights':
                return isFieldFilled(unitPctSubWithoutRights)
            case 'unitPctSubWithoutRights':
                return isFieldFilled(unitSubWithoutRights)
            case 'unitSubGuarantor':
                return isFieldFilled(unitPctSubGuarantor)
            case 'unitPctSubGuarantor':
                return isFieldFilled(unitSubGuarantor)
            default:
                return false
        }
    }

    // When applying a suggestion we write into the form state so the value
    // participates in validation and save logic — this keeps AI suggestions
    // consistent with manual user edits.
    const applySuggestion = (fieldName: string, value: any) => {
        if (value == null) return

        formMethods.setValue(fieldName, value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        })
    }

    return (
        <FormTable>
            <ShouldShow issueType={issue.type} issueField='unitSubTotal'>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label={variableLabel('unitSubTotal', issue)}>
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'flex-start',
                            }}
                        >
                            {allowUnitSubTotal ? (
                                <Stack>
                                    <RegisteredIntegerField
                                        label={variableLabel('unitSubTotal', issue)}
                                        name='unitSubTotal'
                                    />
                                    <FieldExtractionSuggestion
                                        value={formatOutcomeSuggestionValue(
                                            'unitSubTotal',
                                            getOutcomeValueByName('unitSubTotal', suggestionOfferingOutcome),
                                            locale,
                                        )}
                                        onUse={() =>
                                            applySuggestion(
                                                'unitSubTotal',
                                                getOutcomeValueByName('unitSubTotal', suggestionOfferingOutcome),
                                            )
                                        }
                                        provenance={getProvenanceByName('unitSubTotal', suggestionProvenanceByField)}
                                    />
                                </Stack>
                            ) : (
                                <Typography>Inga nya aktier</Typography>
                            )}
                            {['Rights issue', 'IPO'].includes(issue.type) ? (
                                <Stack
                                    sx={{
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant='body2'>Teckningsgrad: {subscriptionQuota}</Typography>
                                    {issue.type !== 'IPO' ? (
                                        <Typography variant='body2'>
                                            Summering av nedan: {formatInteger(calculatedUnitSubTotal, { locale })}{' '}
                                            {calcUnitSubTotalWithinRange !== null && calcUnitSubTotalWithinRange && (
                                                <VerifiedIcon sx={{ fontSize: 14 }} color='success' />
                                            )}
                                            {calcUnitSubTotalWithinRange !== null && !calcUnitSubTotalWithinRange && (
                                                <ErrorIcon fontSize='medium' color='error' />
                                            )}
                                        </Typography>
                                    ) : null}
                                </Stack>
                            ) : null}
                        </Stack>
                    </FormTableRow>
                ) : null}
            </ShouldShow>
            <ShouldShowAny issueType={issue.type} issueFields={['unitSubWithRights', 'unitPctSubWithRights']}>
                <FormTableRow label='Units tecknade med rätter'>
                    <Stack direction='row' spacing={2}>
                        <Stack>
                            <RegisteredIntegerField
                                label='Antal'
                                name='unitSubWithRights'
                                disabled={!!unitPctSubWithRights}
                            />
                            <FieldExtractionSuggestion
                                value={formatOutcomeSuggestionValue(
                                    'unitSubWithRights',
                                    getOutcomeValueByName('unitSubWithRights', suggestionOfferingOutcome),
                                    locale,
                                )}
                                onUse={() =>
                                    applySuggestion(
                                        'unitSubWithRights',
                                        getOutcomeValueByName('unitSubWithRights', suggestionOfferingOutcome),
                                    )
                                }
                                provenance={getProvenanceByName('unitSubWithRights', suggestionProvenanceByField)}
                                useDisabled={isSuggestionUseDisabled('unitSubWithRights')}
                            />
                        </Stack>
                        <Stack>
                            <RegisteredNumericField
                                label='Procent'
                                name='unitPctSubWithRights'
                                minDecimals={0}
                                maxDecimals={2}
                                percentage={true}
                                disabled={!!unitSubWithRights}
                            />
                            <FieldExtractionSuggestion
                                value={formatOutcomeSuggestionValue(
                                    'unitPctSubWithRights',
                                    getOutcomeValueByName('unitPctSubWithRights', suggestionOfferingOutcome),
                                    locale,
                                )}
                                onUse={() =>
                                    applySuggestion(
                                        'unitPctSubWithRights',
                                        getOutcomeValueByName('unitPctSubWithRights', suggestionOfferingOutcome),
                                    )
                                }
                                provenance={getProvenanceByName('unitPctSubWithRights', suggestionProvenanceByField)}
                                useDisabled={isSuggestionUseDisabled('unitPctSubWithRights')}
                            />
                        </Stack>
                    </Stack>
                </FormTableRow>
            </ShouldShowAny>
            <ShouldShowAny issueType={issue.type} issueFields={['unitSubWithoutRights', 'unitPctSubWithoutRights']}>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label='Units tecknade utan rätter'>
                        <Stack direction='row' spacing={2}>
                            <Stack>
                                <RegisteredIntegerField
                                    label='Antal'
                                    name='unitSubWithoutRights'
                                    disabled={!!unitPctSubWithoutRights}
                                />
                                <FieldExtractionSuggestion
                                    value={formatOutcomeSuggestionValue(
                                        'unitSubWithoutRights',
                                        getOutcomeValueByName('unitSubWithoutRights', suggestionOfferingOutcome),
                                        locale,
                                    )}
                                    onUse={() =>
                                        applySuggestion(
                                            'unitSubWithoutRights',
                                            getOutcomeValueByName('unitSubWithoutRights', suggestionOfferingOutcome),
                                        )
                                    }
                                    provenance={getProvenanceByName(
                                        'unitSubWithoutRights',
                                        suggestionProvenanceByField,
                                    )}
                                    useDisabled={isSuggestionUseDisabled('unitSubWithoutRights')}
                                />
                            </Stack>
                            <Stack>
                                <RegisteredNumericField
                                    label='Procent'
                                    name='unitPctSubWithoutRights'
                                    minDecimals={0}
                                    maxDecimals={2}
                                    percentage={true}
                                    disabled={!!unitSubWithoutRights}
                                />
                                <FieldExtractionSuggestion
                                    value={formatOutcomeSuggestionValue(
                                        'unitPctSubWithoutRights',
                                        getOutcomeValueByName('unitPctSubWithoutRights', suggestionOfferingOutcome),
                                        locale,
                                    )}
                                    onUse={() =>
                                        applySuggestion(
                                            'unitPctSubWithoutRights',
                                            getOutcomeValueByName('unitPctSubWithoutRights', suggestionOfferingOutcome),
                                        )
                                    }
                                    provenance={getProvenanceByName(
                                        'unitPctSubWithoutRights',
                                        suggestionProvenanceByField,
                                    )}
                                    useDisabled={isSuggestionUseDisabled('unitPctSubWithoutRights')}
                                />
                            </Stack>
                        </Stack>
                    </FormTableRow>
                ) : null}
            </ShouldShowAny>
            <ShouldShowAny issueType={issue.type} issueFields={['unitSubGuarantor', 'unitPctSubGuarantor']}>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label='Units tecknade av garanter'>
                        {!noGuarantorsChecked && (
                            <Stack direction='row' spacing={2}>
                                <Stack>
                                    <RegisteredIntegerField
                                        label='Antal'
                                        name='unitSubGuarantor'
                                        disabled={!!unitPctSubGuarantor}
                                    />
                                    <FieldExtractionSuggestion
                                        value={formatOutcomeSuggestionValue(
                                            'unitSubGuarantor',
                                            getOutcomeValueByName('unitSubGuarantor', suggestionOfferingOutcome),
                                            locale,
                                        )}
                                        onUse={() =>
                                            applySuggestion(
                                                'unitSubGuarantor',
                                                getOutcomeValueByName('unitSubGuarantor', suggestionOfferingOutcome),
                                            )
                                        }
                                        provenance={getProvenanceByName(
                                            'unitSubGuarantor',
                                            suggestionProvenanceByField,
                                        )}
                                        useDisabled={isSuggestionUseDisabled('unitSubGuarantor')}
                                    />
                                </Stack>
                                <Stack>
                                    <RegisteredNumericField
                                        label='Procent'
                                        name='unitPctSubGuarantor'
                                        minDecimals={0}
                                        maxDecimals={2}
                                        percentage={true}
                                        disabled={!!unitSubGuarantor}
                                    />
                                    <FieldExtractionSuggestion
                                        value={formatOutcomeSuggestionValue(
                                            'unitPctSubGuarantor',
                                            getOutcomeValueByName('unitPctSubGuarantor', suggestionOfferingOutcome),
                                            locale,
                                        )}
                                        onUse={() =>
                                            applySuggestion(
                                                'unitPctSubGuarantor',
                                                getOutcomeValueByName('unitPctSubGuarantor', suggestionOfferingOutcome),
                                            )
                                        }
                                        provenance={getProvenanceByName(
                                            'unitPctSubGuarantor',
                                            suggestionProvenanceByField,
                                        )}
                                        useDisabled={isSuggestionUseDisabled('unitPctSubGuarantor')}
                                    />
                                </Stack>
                            </Stack>
                        )}
                        {noGuarantorsChecked && <Typography>Inga garanter</Typography>}
                    </FormTableRow>
                ) : null}
            </ShouldShowAny>
            <ShouldShow issueType={issue.type} issueField='secondarySub'>
                <FormTableRow label='Befintliga aktier som tecknats (secondary)'>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'flex-start',
                        }}
                    >
                        {!noSecondaryOfferingChecked && (
                            <Stack>
                                <RegisteredIntegerField
                                    label='Antal aktier'
                                    helperText='Utöver de som erbjöds i emissionen'
                                    name='secondarySub'
                                />
                                <FieldExtractionSuggestion
                                    value={formatOutcomeSuggestionValue(
                                        'secondarySub',
                                        getOutcomeValueByName('secondarySub', suggestionOfferingOutcome),
                                        locale,
                                    )}
                                    onUse={() =>
                                        applySuggestion(
                                            'secondarySub',
                                            getOutcomeValueByName('secondarySub', suggestionOfferingOutcome),
                                        )
                                    }
                                    provenance={getProvenanceByName('secondarySub', suggestionProvenanceByField)}
                                />
                            </Stack>
                        )}
                        {noSecondaryOfferingChecked && <Typography>Inga befintliga aktier erbjöds</Typography>}
                    </Stack>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='overAllotmentAllocation'>
                {totalOfferedUnits !== 0 ? (
                    <FormTableRow label='Övertilldelning'>
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'flex-start',
                            }}
                        >
                            {!noOverAllotmentChecked && (
                                <Stack>
                                    <RegisteredIntegerField label='Antal' name='overAllotmentAllocation' />
                                    <FieldExtractionSuggestion
                                        value={formatOutcomeSuggestionValue(
                                            'overAllotmentAllocation',
                                            getOutcomeValueByName('overAllotmentAllocation', suggestionOfferingOutcome),
                                            locale,
                                        )}
                                        onUse={() =>
                                            applySuggestion(
                                                'overAllotmentAllocation',
                                                getOutcomeValueByName(
                                                    'overAllotmentAllocation',
                                                    suggestionOfferingOutcome,
                                                ),
                                            )
                                        }
                                        provenance={getProvenanceByName(
                                            'overAllotmentAllocation',
                                            suggestionProvenanceByField,
                                        )}
                                    />
                                </Stack>
                            )}
                            {noOverAllotmentChecked && <Typography>Ingen övertilldelning</Typography>}
                        </Stack>
                    </FormTableRow>
                ) : null}
            </ShouldShow>
            <ShouldShow issueType={issue.type} issueField='overAllotmentAllocationSecondary'>
                {!noOverAllotmentChecked ? (
                    <FormTableRow label='Övertilldelning (secondary)'>
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'flex-start',
                            }}
                        >
                            {overAllotmentSizeSecondary && overAllotmentSizeSecondary !== 0 ? (
                                <Stack>
                                    <RegisteredIntegerField label='Antal' name='overAllotmentAllocationSecondary' />
                                    <FieldExtractionSuggestion
                                        value={formatOutcomeSuggestionValue(
                                            'overAllotmentAllocationSecondary',
                                            getOutcomeValueByName(
                                                'overAllotmentAllocationSecondary',
                                                suggestionOfferingOutcome,
                                            ),
                                            locale,
                                        )}
                                        onUse={() =>
                                            applySuggestion(
                                                'overAllotmentAllocationSecondary',
                                                getOutcomeValueByName(
                                                    'overAllotmentAllocationSecondary',
                                                    suggestionOfferingOutcome,
                                                ),
                                            )
                                        }
                                        provenance={getProvenanceByName(
                                            'overAllotmentAllocationSecondary',
                                            suggestionProvenanceByField,
                                        )}
                                    />
                                </Stack>
                            ) : (
                                <Typography>Ingen övertilldelning (secondary)</Typography>
                            )}
                        </Stack>
                    </FormTableRow>
                ) : null}
            </ShouldShow>
        </FormTable>
    )
}

export default OutcomeFields
